'use client'
import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Image } from '@src/quince-components/src'
import LoaderWrapper from '@src/quince-components/src/atoms/loaderWrapper/LoaderWrapper'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { useCurrentUser } from '@lastbrand/fe-common/universal-auth'
import useReferralPoints from '@src/quince-site-integrated/src/hooks/useReferralPoints'
import { extractQueryParamFromPageLocation } from '../../utils'
import useSwellSDK from '@src/quince-site-integrated/src/hooks/useSwellSDK'
import AccountWrapper from '../../components/Account/AccountWrapper'
import PageHeader from '../../components/Account/PageHeader'
import Button from '../../components/Button'
import Modal from '../../components/Modal'
import ReferralPoints from '../../components/ReferAndEarn/ReferralPoints'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import Divider from '../../components/common/Divider'
import { API_SF_RUNTIME_BASE_URL, API_LOYALTY_SERVICE_ENDPOINT } from '@src/config/env'
import useBearerFetch from '../../hooks/useBearerFetch'
import styles from './my-rewards.module.scss'
import { PAGE_PATH } from '@src/quince-site-common/src/constants/pageData'
import { useQuinceRouter } from '@src/modules/navigation'
import { useSearchParams } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import useCustomerSearch from '@src/quince-site-integrated/src/hooks/useCustomerSearch'

const MyRewards = ({ data }) => {
  const { t } = useTranslation('accountPage')
  const router = useQuinceRouter()
  const params = useSearchParams().toString()
  const page = data.accountPage
  const { email } = useCurrentUser()

  const { response, fetchData } = useBearerFetch()
  const { fetchData: fetchRedemption } = useBearerFetch()
  const { fetchData: addCustomerTagsFetch } = useBearerFetch()

  const { fetchReferrals, successfulReferrals, invitedReferrals } = useReferralPoints(email)

  const queryParams = isBrowser() ? params : ''

  // Use the unified hook that handles both APIs based on the flag
  const { customerId } = useCustomerSearch()

  async function updateUrl({ param, value }, removeParam) {
    const searchParams = new URLSearchParams(queryParams)
    if (removeParam) {
      searchParams.delete(param)
    } else {
      searchParams.set(param, value)
    }
    const updatedSearch = searchParams.toString()
    const sanitizedUpdatedSearch = updatedSearch ? `?${updatedSearch}` : ''
    const newPath = `/${PAGE_PATH.MY_REWARDS}${sanitizedUpdatedSearch}`
    await router.replace(newPath, newPath, { shallow: true })
  }

  const handleOnPopupClose = () => {
    if (extractQueryParamFromPageLocation('open_rewards_popup') === 'true') {
      updateUrl({ param: 'open_rewards_popup' }, true)
    } else if (extractQueryParamFromPageLocation('open_earn_rewards_popup') === 'true') {
      updateUrl({ param: 'open_earn_rewards_popup' }, true)
    }
  }

  const { showPopUp, setup, initialized } = useSwellSDK(email, customerId, undefined, handleOnPopupClose)

  const hideBanner = () => {
    const yotpoIframe = document.getElementById('swell-popup')
    if (!yotpoIframe) return // Exit if not found
    try {
      const iframeDoc = yotpoIframe.contentDocument || yotpoIframe.contentWindow.document

      // to hide iframe of google translate after loading
      const childIframes = iframeDoc.querySelectorAll('iframe')

      childIframes.forEach(childIframe => {
        childIframe.style.display = 'none'

        if (/^:\d+\.container$/.test(childIframe.id)) {
          childIframe.style.display = 'none'
        }
      })
    } catch (error) {
      console.error('Cannot access iframe content (likely cross-origin):', error)
    }
  }

  const isSwellReady = setup && initialized && customerId

  const [dataLoader, setDataLoader] = useState(true)
  const [showSwellLoader, setSwellLoader] = useState(false)

  function trackCloseSwellPopup(actionType) {
    trackEvent(TELEMETRY_EVENT_NAMES.MODAL_CLOSE, {
      action: actionType,
    })
  }

  const addSwellPopUpCloseListener = useCallback(actionType => {
    const iframe = document.querySelector('iframe#swell-popup')
    const iDoc = iframe?.contentWindow || iframe?.contentDocument
    iDoc?.document?.querySelector('.hide-popup')?.addEventListener('click', function () {
      trackCloseSwellPopup(actionType)
    })
  }, [])

  const updateRewardPopupFontFamily = useCallback((count = 0) => {
    const rewardPopup = document.querySelector('iframe#swell-popup')
    const rewardPopupDocument = rewardPopup?.contentDocument
    const swellRewardsNav = rewardPopupDocument?.getElementById('swell-rewards-nav')
    if (rewardPopupDocument && swellRewardsNav) {
      const popupBody = rewardPopupDocument.body
      popupBody.setAttribute('style', 'font-family: grosa,sans-serif !important')

      const swellHeaders = popupBody.getElementsByClassName('swell-header')
      if (swellHeaders[0]) {
        swellHeaders[0].setAttribute(
          'style',
          'font-family: grosa,sans-serif !important; font-size: 16px; font-weight: 500; letter-spacing: 0.05em; text-align: center;',
        )
      }

      const swellRewardsNavAnchorTags = swellRewardsNav?.getElementsByTagName('a')
      for (let i = 0; i < swellRewardsNavAnchorTags?.length || 0; i += 1) {
        swellRewardsNavAnchorTags[i].setAttribute(
          'style',
          'font-family: grosa,sans-serif !important; font-size: 14px; font-weight: 500;',
        )
      }

      const h2Els = rewardPopupDocument.getElementsByTagName('h2')
      for (let i = 0; i < h2Els?.length || 0; i += 1) {
        h2Els[i].setAttribute(
          'style',
          'font-family: grosa,sans-serif !important; font-size: 22px; font-weight: 500; padding: 14px 10px 14px 25px;',
        )
      }

      const h3Els = rewardPopupDocument.getElementsByTagName('h3')
      for (let i = 0; i < h3Els?.length || 0; i += 1) {
        h3Els[i].setAttribute('style', 'font-family: ivy-headline, serif !important')
      }

      const h4Els = rewardPopupDocument.getElementsByTagName('h4')
      for (let i = 0; i < h4Els?.length || 0; i += 1) {
        h4Els[i].setAttribute('style', 'font-family: grosa,sans-serif !important; font-weight: 600;')
      }

      const pEls = rewardPopupDocument.getElementsByTagName('p')
      for (let i = 0; i < pEls?.length || 0; i += 1) {
        pEls[i].setAttribute('style', 'font-family: ivy-text, serif !important; font-weight: normal;')
      }

      const tableStripedEls = rewardPopupDocument.getElementsByClassName('table-striped')
      const tableStripedEl = tableStripedEls[0]
      if (tableStripedEl) {
        const thEls = tableStripedEl.getElementsByTagName('th')
        for (let i = 0; i < thEls.length; i += 1) {
          thEls[i].setAttribute(
            'style',
            'font-family: ivy-text, serif !important; font-weight: normal; font-size: 23px;',
          )
        }
      }

      const pointsDivEls = rewardPopupDocument.getElementsByClassName('points')
      const pointDivEl = pointsDivEls[0]
      if (pointDivEl) {
        pointDivEl.setAttribute('style', 'text-align: center;')
      }

      const referralLinkEls = rewardPopupDocument.getElementsByClassName('swell-referral-link')
      const referralLinkEl = referralLinkEls[0]
      if (referralLinkEl) {
        referralLinkEl.setAttribute(
          'style',
          'text-transform: uppercase; font-size: 15px; padding-top: 12px; padding-bottom: 10px;',
        )
      }

      const shareReferralLinksEls = rewardPopupDocument.getElementsByClassName('swell-referral-share-links')
      const shareReferralLinksEl = shareReferralLinksEls[0]
      if (shareReferralLinksEl) {
        const aTags = shareReferralLinksEl.getElementsByTagName('a')
        for (let i = 0; i < aTags.length; i += 1) {
          const aTag = aTags[i]
          aTag.setAttribute('style', 'font-size: 16px;')
        }
      }

      const campaignPanelBodyEls = rewardPopupDocument.getElementsByClassName('swell-campaign-panel-body')
      for (let i = 0; i < campaignPanelBodyEls; i += 1) {
        const campaignPanelBodyEl = campaignPanelBodyEls[i]
        const campaignPanelBodyPEls = campaignPanelBodyEl.getElementsByTagName('p')
        const campaignPanelBodyPEl = campaignPanelBodyPEls[0]
        if (campaignPanelBodyPEl) {
          campaignPanelBodyPEl.setAttribute('style', 'margin-top: 20px;')
        }
      }
    } else {
      if (count < 5) {
        setTimeout(
          () => {
            updateRewardPopupFontFamily(count + 1)
          },
          300 * (count + 1),
        )
      }
    }
  }, [])

  const updateRewardsPopupLink = () => {
    updateUrl({
      param: 'open_rewards_popup',
      // @i18n-ignore
      value: 'true',
    })
  }

  const openRewardsPopup = useCallback(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.MODAL_OPEN, {
      action: 'rewards_popup',
    })

    showPopUp('redeem')
    updateRewardPopupFontFamily()
    setTimeout(() => {
      addSwellPopUpCloseListener('rewards_popup')
    }, 1000)
  }, [showPopUp, updateRewardPopupFontFamily, addSwellPopUpCloseListener])

  const updateSwellPopupLink = () => {
    updateUrl({
      param: 'open_earn_rewards_popup',
      // @i18n-ignore
      value: 'true',
    })
  }

  const handleEarnPointsClick = () => {
    if (!isSwellReady) {
      setSwellLoader(true)
      updateUrl({
        param: 'open_earn_rewards_popup',
        // @i18n-ignore
        value: 'true',
      })
      return
    }
    updateSwellPopupLink()
    openSwellPopup()
  }

  const openSwellPopup = useCallback(() => {
    trackEvent(TELEMETRY_EVENT_NAMES.MODAL_OPEN, {
      action: 'rewards_swell_popup',
    })
    showPopUp()
    updateRewardPopupFontFamily()

    // @i18n-ignore
    const url = `${API_SF_RUNTIME_BASE_URL}/update-customer-tags`
    const options = {
      method: 'POST',
      body: JSON.stringify({ tags: ['VIEWED_LOYALTY'] }),
    }
    addCustomerTagsFetch(url, options)
    setTimeout(() => {
      hideBanner()
    }, 3000)
    setTimeout(() => {
      addSwellPopUpCloseListener('rewards_swell_popup')
    }, 1000)
  }, [addCustomerTagsFetch, addSwellPopUpCloseListener, showPopUp, updateRewardPopupFontFamily])

  useEffect(() => {
    if (customerId) {
      fetchData(`${API_LOYALTY_SERVICE_ENDPOINT}/points/user-balance`)
    }
    fetchRedemption(`${API_SF_RUNTIME_BASE_URL}/redemption-options`)
  }, [customerId, fetchData, fetchRedemption])

  useEffect(() => {
    const hasRewardsPopup = extractQueryParamFromPageLocation('open_rewards_popup') === 'true'
    if (hasRewardsPopup) {
      if (!isSwellReady) {
        setSwellLoader(true)
        return
      }
      setSwellLoader(false)
      openRewardsPopup()
    }
  }, [openRewardsPopup, queryParams, isSwellReady])

  useEffect(() => {
    const hasSwellPopup = extractQueryParamFromPageLocation('open_earn_rewards_popup') === 'true'

    if (hasSwellPopup) {
      if (!isSwellReady) {
        setSwellLoader(true)
        return
      }
      setSwellLoader(false)
      openSwellPopup()
    }
  }, [openSwellPopup, queryParams, isSwellReady])

  useEffect(() => {
    fetchReferrals()
  }, [fetchReferrals])

  useEffect(() => {
    if (!successfulReferrals) return

    setDataLoader(false)
  }, [successfulReferrals])

  // #endregion

  return (
    <AccountWrapper heroImage={page.hero} menu={page.menuList} contact={page.contact}>
      <PageHeader title={page.title} />
      <Modal className={styles.styledModal} open={showSwellLoader} showCloseBtn={false}>
        <LoaderWrapper
          showLoader
          containerClassName={styles.modal_loader}
          loadWrapperClassName={styles.modal_loadWrapper}
        />
      </Modal>
      <div className={styles.container}>
        <div className={styles.styledDivider}>
          <Divider />
        </div>
        <div className={styles.summary}>
          <div className={styles.summaryContent}>
            <div className={styles.summaryText}>
              {!isSwellReady && <div className={styles.loader} />}
              {!dataLoader &&
                isSwellReady &&
                (response?.balancePoints <= 0 || !response?.balancePoints ? (
                  <p>{t('earnPointsDesc')}</p>
                ) : (
                  <>
                    <p style={{ fontWeight: 500 }}>{t('pointsEarned', { value: response.balancePoints })} </p>
                    <p> {t('discountDesc')} </p>
                  </>
                ))}
            </div>
            {/* {!response?.referral_code ? (
               <StyledLoader />
              ) : (
               <LinkContainer>
                 http://rwrd.io/{response?.referral_code}
               </LinkContainer>
              )} */}
            <div className={styles.redeemPointsButtonWrapper}>
              {response?.balancePoints > 0 && isSwellReady && (
                <Button
                  className={`${styles.redeemPointsButton}`}
                  onClick={updateRewardsPopupLink}
                  trackParams={{
                    trackInternal: true,
                    trackGA: true,
                    // @i18n-ignore
                    category: 'referral',
                    action: 'click_redeem_points',
                  }}
                >
                  {t('redeemPoints')}
                </Button>
              )}
              {isSwellReady && (
                <Button
                  className={classNames(styles.redeemPointsButton, {
                    [styles.onlyButton]: response?.balancePoints === 0,
                    [styles.lightButton]: response?.balancePoints !== 0,
                  })}
                  onClick={handleEarnPointsClick}
                  trackParams={{
                    trackInternal: true,
                    trackGA: true,
                    // @i18n-ignore
                    category: 'referral',
                    action: 'click_earn_points',
                  }}
                >
                  {t('earnPoints')}
                </Button>
              )}
            </div>
            <Button
              className={`${styles.styledButton}`}
              cantaloupe
              to="/refer-and-earn?trafficSource=my-rewards"
              trackParams={{
                trackInternal: true,
                trackGA: true,
                // @i18n-ignore
                category: 'referral',
                action: 'click_invite_friends',
              }}
            >
              {t('inviteDesc', { value: `${t('currency')}20*` })}
            </Button>
            {
              <div className={styles.conditionsText}>
                <p>
                  {t('termsDesc', { value: `${t('currency')}100` })}
                  <a className={styles.anchor} href="https://www.quince.com/referral-program-terms-and-conditions">
                    {t('termsApply')}
                  </a>
                  .
                </p>
              </div>
            }
          </div>
          <div className={styles.featuredImageContainer}>
            <Image
              className={styles.featuredImage}
              url={page.featuredImage.file.url}
              title={page.featuredImage.title}
              alt={page.featuredImage.description}
            />
            {page?.text && <p>{page.text}</p>}
          </div>
        </div>
        <div className={styles.referralPointsWrapper}>
          <ReferralPoints
            title={<div className={styles.referralPointsHeader}>{t('referedFriendsDesc')} </div>}
            referrals={successfulReferrals || []}
            totalText={t('totalPoints', { type: t('earned') })}
            noReferralsMsg={t('noReferrals')}
          />
        </div>
        <div className={styles.referralPointsWrapper}>
          <ReferralPoints
            title={<div className={styles.referralPointsHeader}>{t('yourReferrals')} </div>}
            referrals={invitedReferrals || []}
            totalText={t('totalPoints', { type: t('potential') })} //"Total potential points"
            noReferralsMsg={t('noInvitedReferrals')}
          />
        </div>
      </div>
    </AccountWrapper>
  )
}

export default MyRewards
